import { useLocation } from 'react-router-dom';

import { ISubMenu } from '@/components/SideMenu';
import { useUser } from '@/context/authenticatedUser';
import { useIsCustomerManager } from '@/hooks/customerManagerToken';
import { useTranslate } from '@/hooks/translate';

export enum ADMINROUTE {
  CREATE = '/admins/create',
  DELETE = '/admins/delete',
  SYS_ADMIN_TOKEN = '/admins/system-admin-token',
  CUSTOMER_MANAGER_TOKEN = '/admins/customer-manager-token',
}

export enum CUSTOMERROUTE {
  CUSTOMERS = '/customers',
  IN_QUEUE = '/customers/queue',
  CREATE = '/customers/create',
  IN_PROGRESS = '/customers/create/in-progress',
  MODULES = '/customers/modules',
}

export enum SUPPORTROUTE {
  NG_LINK = '/support/nglink',
  CLASSIC_SSO = '/support/classic-sso',
}

const useAdminMenuTree = (): ISubMenu => {
  const { t } = useTranslate();

  return {
    key: 'SysAdmin',
    title: t('sidebar.admin.systemAdmin'),
    links: [
      {
        id: 'create',
        route: ADMINROUTE.CREATE,
        linkLabel: t('common.create'),
      },
      {
        id: 'delete',
        route: ADMINROUTE.DELETE,
        linkLabel: t('admin.revokeAccess'),
      },
      {
        id: 'token',
        route: ADMINROUTE.SYS_ADMIN_TOKEN,
        linkLabel: t('sidebar.admin.token'),
      },
    ],
  };
};

const useCustomerManagerMenuTree = (): ISubMenu => {
  const { t } = useTranslate();

  return {
    key: 'CustomerManager',
    title: t('sidebar.admin.customerManager'),
    links: [
      {
        id: 'token',
        route: ADMINROUTE.CUSTOMER_MANAGER_TOKEN,
        linkLabel: t('sidebar.admin.token'),
      },
    ],
  };
};

const useCustomerMenuTree = (): ISubMenu => {
  const { t } = useTranslate();
  const { systemAdmin } = useUser();

  return {
    key: 'customerCrud',
    title: t('sidebar.customer.customers'),
    links: [
      {
        id: 'list',
        route: CUSTOMERROUTE.CUSTOMERS,
        linkLabel: t('sidebar.customer.list'),
      },
      {
        id: 'create',
        route: CUSTOMERROUTE.CREATE,
        linkLabel: t('common.create'),
      },
      {
        id: 'creation-queue',
        route: CUSTOMERROUTE.IN_PROGRESS,
        linkLabel: t('sidebar.customer.creationQueue'),
        hidden: !systemAdmin,
      },
      {
        id: 'deletion-queue',
        route: CUSTOMERROUTE.IN_QUEUE,
        linkLabel: t('sidebar.customer.deletionQueue'),
        hidden: !systemAdmin,
      },
      {
        id: 'bulk-edit-modules',
        route: CUSTOMERROUTE.MODULES,
        linkLabel: t('sidebar.customer.bulkEditModules'),
        hidden: !systemAdmin,
      },
    ],
  };
};

const useSupportMenuTree = (): ISubMenu => {
  const { t } = useTranslate();

  return {
    key: 'Support',
    title: t('sidebar.support.support'),
    links: [
      {
        id: 'ng-link',
        route: SUPPORTROUTE.NG_LINK,
        linkLabel: t('sidebar.support.ngLink'),
      },
      {
        id: 'classic-sso',
        route: SUPPORTROUTE.CLASSIC_SSO,
        linkLabel: t('sidebar.support.classicSso'),
      },
    ],
  };
};

/** Side menu for the current section (desktop aside). */
export const useSideMenuTree = (): ISubMenu[] => {
  const { pathname } = useLocation();
  const section = pathname.split('/')[1];
  const { systemAdmin } = useUser();
  const adminMenu = useAdminMenuTree();
  const customerManagerMenu = useCustomerManagerMenuTree();
  const customerMenu = useCustomerMenuTree();
  const supportMenu = useSupportMenuTree();

  if (section === 'admins')
    return systemAdmin
      ? [adminMenu, customerManagerMenu]
      : [customerManagerMenu];
  if (section === 'customers') return [customerMenu];
  if (section === 'support') return [supportMenu];
  return [];
};

/** All section menus for mobile navigation. */
export const useAllSideMenuTrees = (): ISubMenu[] => {
  const { systemAdmin } = useUser();
  const { isCustomerManager } = useIsCustomerManager();
  const adminMenu = useAdminMenuTree();
  const customerManagerMenu = useCustomerManagerMenuTree();
  const customerMenu = useCustomerMenuTree();
  const supportMenu = useSupportMenuTree();

  return [
    ...(systemAdmin ? [adminMenu] : []),
    ...(systemAdmin || isCustomerManager ? [customerManagerMenu] : []),
    customerMenu,
    ...(systemAdmin ? [supportMenu] : []),
  ];
};
